import { ApplicationInsightsEventEnum } from "@/lib/enum/application-insights-event.enum";
import { restoreFromLocalStorage } from "@/lib/restoreFromLocalStorage";
import store from "@/store/VuexPlugin";
import { appInsights } from "@/lib/utility/app-insights";
import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { PartnerModule } from "./partner";

@Module({
  dynamic: true,
  namespaced: true,
  name: "feature",
  store
})
export class FeatureStore extends VuexModule {
  @Action
  trackFeatureFlag({ flag, value }: { flag: string; value: boolean }) {
    appInsights.trackEvent(
      { name: ApplicationInsightsEventEnum.TOGGLE_FEATURE_FLAG },
      { partnerId: PartnerModule.partner?.id, flag, value }
    );
  }

  /**
   * Get the current value of the interactive camera setting.
   */
  get isInteractiveCameraEnabled(): boolean {
    return true;
  }

  /**
   * Get the current value of the Pagination setting.
   */
  get isPaginationEnabled(): boolean {
    return true;
  }

  /**
   * Get the current value of the progressStatusEnabled setting.
   */
  get isUpdateProgressStatusEnabled(): boolean {
    return true;
  }

  /**
   * True if settings control is enabled
   */
  get isPartnerManagerEnabled(): boolean {
    return true;
  }

  /**
   * True if partner analytics is enabled.
   */
  get isPartnerAnalyticsEnabled(): boolean {
    return true;
  }

  /**
   * True if multipartner analytics is enabled.
   */
  get isMultipartnerAnalyticsEnabled(): boolean {
    return true;
  }

  /**
   * Get the current value of the isPdfUploadEnabled
   */
  get isPdfUploadEnabled(): boolean {
    return true;
  }

  /**
   * Get the current value of the template setting.
   */
  get isTemplateEnabled(): boolean {
    return true;
  }

  _fleet: IFleetFeature = {
    isAppointmentEnabled: true
  };

  /**
   * Get the current value of the fleet.
   */
  get isFleetFeatureEnabled(): IFleetFeature {
    return this._fleet;
  }

  get isContactsAsDriverEnabled(): boolean {
    return true;
  }

  @Action
  public setIsContactsAsDriverEnabled(value: boolean) {
    this.context.commit("_mutateIsContactsAsDriverEnabledV2", value);
  }

  @Mutation
  private _mutateIsContactsAsDriverEnabledV2(value: boolean) {
    this._isContactsAsDriverEnabledV2 = value;
  }

  _claimSettlementAnalyticsEnabled = false;

  get isClaimSettlementAnalyticsEnabled(): boolean {
    return this._claimSettlementAnalyticsEnabled;
  }

  @Mutation
  private _mutateClaimSettlementAnalyticsEnabled(value: boolean) {
    this._claimSettlementAnalyticsEnabled = value;
  }

  @Action
  public setIsClaimSettlementAnalyticsEnabled(value: boolean) {
    this.context.commit("_mutateClaimSettlementAnalyticsEnabled", value);
  }

  _isReportNewMobileView = restoreFromLocalStorage("_isReportNewMobileView", "feature", false);

  get isReportNewMobileView(): boolean {
    return true;
  }

  @Mutation
  private _mutateIsReportNewMobileView(value: boolean) {
    this._isReportNewMobileView = value;
  }

  @Action
  public setIsReportNewMobileView(value: boolean) {
    this.context.commit("_mutateIsReportNewMobileView", value);
  }

  @Mutation
  private _mutateIsFleetFeatureEnabled(fleetFeatureEnabled: IFleetFeature) {
    this._fleet = fleetFeatureEnabled;
  }

  @Action
  public setIsAppointmentEnabled(isAppointmentEnabled: boolean) {
    const fleet = this.isFleetFeatureEnabled;
    fleet.isAppointmentEnabled = isAppointmentEnabled;
    this.context.commit("_mutateIsFleetFeatureEnabled", fleet);
  }

  /**
   * Get the current value of the signature.
   */
  get isDigitalSignatureEnabled(): IDigitalSignatureFeature {
    const signatureFeature: IDigitalSignatureFeature = {
      isSignEnabled: true
    };
    return signatureFeature;
  }

  /**
   * Get the current value of the report.
   */
  get isReportEnabled(): IReportFeature {
    const report: IReportFeature = {
      isSharingEnabled: true,
      isTimelineEnabled: true
    };

    return report;
  }

  /**
   * Get the current default value of the marketplace.
   */
  get isMarketplaceEnabled(): IMarketplaceFeature {
    const isMarketplaceEnabled: IMarketplaceFeature = {
      isMarketplaceEnabled: true,
      isInsuranceCheckEnabled: true
    };

    return isMarketplaceEnabled;
  }

  /**
   * Setting for reportAdvertismentEnabled.
   */
  private _isReportAdvertisementEnabled = restoreFromLocalStorage("_isReportAdvertisementEnabled", "feature", false);

  /**
   * Get the current value of the reportAdvertismentEnabled setting.
   */
  get isReportAdvertisementEnabled(): boolean {
    return this._isReportAdvertisementEnabled;
  }

  /**
   * Dispatches an action to set the report advetisement enabled property.
   *
   * @param value
   */
  @Action
  setReportAdvertisementEnabled(value: boolean) {
    this.context.commit("updateReportAdvertisementEnabled", value);
  }

  /**
   * Mutates the report advetisement enabled property.
   *
   * @param value
   */
  @Mutation
  updateReportAdvertisementEnabled(value: boolean) {
    this._isReportAdvertisementEnabled = value;
  }

  /**
   * Get the current value of the isInviteFriendsEnabled setting.
   */
  get isInviteFriendsEnabled(): boolean {
    return true;
  }

  /**
   * Setting for showing the renewable energy flag in the charging station form
   * and upload of proofs
   */
  private _isRenewableChargingStationEnabled = restoreFromLocalStorage(
    "_isRenewableChargingStationEnabled",
    "feature",
    true
  );

  /**
   * Get the setting for showing renewable energy flag in the charging station form
   */
  get isRenewableChargingStationEnabled(): boolean {
    return this._isRenewableChargingStationEnabled;
  }

  /**
   * Dispatches an action to set setting for showing renewable energy flag in the charging station form
   *
   * @param value
   */
  @Action
  public setIsRenewableChargingStationEnabled(value: boolean) {
    this.context.commit("_mutateIsRenewableChargingStationEnabled", value);
  }

  /**
   * Mutates the setting for showing renewable energy flag in the charging station form
   *
   * @param value
   */
  @Mutation
  private _mutateIsRenewableChargingStationEnabled(value: boolean) {
    this._isRenewableChargingStationEnabled = value;
  }

  /**
   * Setting for partner colors
   */
  get isVuetifyInPartnerColors() {
    return PartnerModule.partner.settings?.isVuetifyInPartnerColors ?? false;
  }
  /**
   * Setting for partner colors
   */
  get isNavigationInPartnerColors() {
    return PartnerModule.partner.settings?.isNavigationInPartnerColors ?? false;
  }

  /**
   * Get the current value of the online booking feature flag.
   */
  get onlineBooking(): boolean {
    return true;
  }

  /**
   * Setting for upload attachments for online booking
   */
  private _attachmentsOnlineBooking: boolean = restoreFromLocalStorage("_attachmentsOnlineBooking", "feature", false);

  get attachmentsOnlineBooking(): boolean {
    return this._attachmentsOnlineBooking;
  }

  @Mutation
  private _mutateSetAttachmentsOnlineBooking(value: boolean) {
    this._attachmentsOnlineBooking = value;
  }

  @Action
  public setAttachmentsOnlineBooking(value: boolean) {
    this.context.commit("_mutateSetAttachmentsOnlineBooking", value);
  }

  /**
   * Setting on billing page to check if the billing distribution is enabled
   */
  private _isBillingDistributionEnabled: boolean = restoreFromLocalStorage(
    "_isBillingDistributionEnabled",
    "feature",
    false
  );
  get isBillingDistributionEnabled(): boolean {
    return this._isBillingDistributionEnabled;
  }
  @Mutation
  private _mutateIsBillingDistributionEnabled(value: boolean) {
    this._isBillingDistributionEnabled = value;
  }
  @Action
  public setIsBillingDistributionEnabled(value: boolean) {
    this.context.commit("_mutateIsBillingDistributionEnabled", value);
  }
  /**
   * Feature Flag for billing overview for partner
   */
  private _isPartnerBillingOverviewEnabled = true;
  get isPartnerBillingOverviewEnabled(): boolean {
    return this._isPartnerBillingOverviewEnabled;
  }
  @Mutation
  private _mutateIsPartnerBillingOverviewEnabled(value: boolean) {
    this._isPartnerBillingOverviewEnabled = value;
  }
  @Action
  public setIsPartnerBillingOverviewEnabled(value: boolean) {
    this.context.commit("_mutateIsPartnerBillingOverviewEnabled", value);
  }
  /**
   * Feature Flag for uploading documents in the report
   */
  private _isDocumentUploadEnabled = true;
  get isDocumentUploadEnabled(): boolean {
    return this._isDocumentUploadEnabled;
  }
  @Mutation
  private _mutateIsDocumentUploadEnabled(value: boolean) {
    this._isDocumentUploadEnabled = value;
  }
  @Action
  public setIsDocumentUploadEnabled(value: boolean) {
    this.context.commit("_mutateIsDocumentUploadEnabled", value);
  }

  /**
   * Setting for showing option to isInboxEnabled
   */
  private _isInboxEnabled = restoreFromLocalStorage("_isInboxEnabled", "feature", false);

  /**
   * Get the current value of the isInboxEnabled
   */
  get isInboxEnabled(): boolean {
    return true;
  }

  /**
   * Dispatches an action to enable the option to export to ksr
   *
   * @param value The value to set the option to.
   */
  @Action
  public setIsInboxEnabled(value: boolean) {
    this.context.commit("_mutateInboxEnabled", value);
  }

  /**
   * Mutates the option to export to ksr
   *
   * @param value The value to set the option to export to ksr.
   */
  @Mutation
  private _mutateInboxEnabled(value: boolean) {
    this._isInboxEnabled = value;
  }

  /**
   * Setting for showing option to enable new signature form
   */
  private _isSignatureFormActive = restoreFromLocalStorage("_isSignatureFormActive", "feature", false);

  /**
   * Get the current value of option to enable new signature form
   */
  get isSignatureFormActive(): boolean {
    return true;
  }

  /**
   * Dispatches an action to enable new signature form
   *
   * @param value The value to set the option to.
   */
  @Action
  public setIsSignatureFormActive(value: boolean) {
    this.context.commit("_mutateIsSignatureFormActive", value);
  }

  /**
   * Mutates the option to use ticket
   *
   * @param valu
   */
  @Mutation
  private _mutateIsSignatureFormActive(value: boolean) {
    this._isSignatureFormActive = value;
  }

  /**
   * Setting for showing option to enable ticket
   */
  private _isTicketEnabled = restoreFromLocalStorage("_isTicketEnabled", "feature", false);

  /**
   * Get the current value of option to enable  ticket
   */
  get isTicketEnabled(): boolean {
    return this._isTicketEnabled;
  }

  /**
   * Dispatches an action to enable  ticket
   *
   * @param value The value to set the option to.
   */
  @Action
  public setIsTicketEnabled(value: boolean) {
    this.context.commit("_mutateIsTicketEnabled", value);
  }

  /**
   * Mutates the option to use ticket
   *
   * @param value
   */
  @Mutation
  private _mutateIsTicketEnabled(value: boolean) {
    this._isTicketEnabled = value;
  }

  /**
   * Setting for showing option to enable ticket
   */
  private _isEvseEnabled = restoreFromLocalStorage("_isEvseEnabled", "feature", false);

  /**
   * Get the current value of option to enable  ticket
   */
  get isEvseEnabled(): boolean {
    return this._isEvseEnabled;
  }

  /**
   * Dispatches an action to enable  ticket
   *
   * @param value The value to set the option to.
   */
  @Action
  public setIsEvseEnabled(value: boolean) {
    this.context.commit("_mutateIsEvseEnabled", value);
  }

  /**
   * Mutates the option to use evse
   *
   * @param value
   */
  @Mutation
  private _mutateIsEvseEnabled(value: boolean) {
    this._isEvseEnabled = value;
  }

  /**
   * Setting for showing option to enable map
   */
  private _isMapShown = restoreFromLocalStorage("_isMapShown", "feature", false);

  /**
   * Get the current value of option to enable map
   */
  get isMapShown(): boolean {
    return this._isMapShown;
  }

  /**
   * Dispatches an action to enable map
   *
   * @param value The value to set the option to.
   */
  @Action
  public setIsMapShown(value: boolean) {
    this.context.commit("_mutateIsMapShown", value);
  }

  /**
   * Mutates the option to use evse
   *
   * @param value
   */
  @Mutation
  private _mutateIsMapShown(value: boolean) {
    this._isMapShown = value;
  }

  /**
   * Setting for showing option to hash email in gtm
   */
  private _isGtmHashEmail = restoreFromLocalStorage("_isGtmHashEmail", "feature", false);

  /**
   * Enables email hashing in GTM store
   */
  get isGtmHashEmail(): boolean {
    return this._isGtmHashEmail;
  }

  @Action
  public setIsGtmHashEmail(value: boolean) {
    this.context.commit("_mutateIsGtmHashEmail", value);
  }

  @Mutation
  private _mutateIsGtmHashEmail(value: boolean) {
    this._isGtmHashEmail = value;
  }

  private _isCsvExport = restoreFromLocalStorage("_isCsvExport", "feature", false);

  get isCsvExport() {
    return this._isCsvExport || true;
  }

  @Action
  public setIsCsvExport(value: boolean) {
    this.context.commit("_mutateIsCsvExport", value);
  }

  @Mutation
  private _mutateIsCsvExport(value: boolean) {
    this._isCsvExport = value;
  }

  private _isQrCodeMultipleDownload = false;

  get isQrCodeMultipleDownload() {
    return this._isQrCodeMultipleDownload;
  }

  @Action
  public setIsQrCodeMultipleDownload(value: boolean) {
    this.context.commit("_mutateIsQrCodeMultipleDownload", value);
  }

  @Mutation
  private _mutateIsQrCodeMultipleDownload(value: boolean) {
    this._isQrCodeMultipleDownload = value;
  }

  private _isExtendedAcknowledgement = true;

  get isExtendedAcknowledgement() {
    return this._isExtendedAcknowledgement;
  }

  @Action
  public setIsExtendedAcknowledgement(value: boolean) {
    this.context.commit("_mutateIsExtendedAcknowledgement", value);
  }

  @Mutation
  private _mutateIsExtendedAcknowledgement(value: boolean) {
    this._isExtendedAcknowledgement = value;
  }

  private _isNewVehicleView = false;

  get isNewVehicleView() {
    return this._isNewVehicleView;
  }

  @Action
  public setIsNewVehicleView(value: boolean) {
    this.context.commit("_mutateIsNewVehicleView", value);
  }

  @Mutation
  private _mutateIsNewVehicleView(value: boolean) {
    this._isNewVehicleView = value;
  }

  private _isFileSelectionOnShortPressInImageUpload = restoreFromLocalStorage(
    "_isFileSelectionOnShortPressInImageUpload",
    "feature",
    false
  );

  get isFileSelectionOnShortPressInImageUpload() {
    return this._isFileSelectionOnShortPressInImageUpload;
  }

  @Action
  public setIsFileSelectionOnShortPressInImageUpload(value: boolean) {
    this.context.commit("_mutateIsFileSelectionOnShortPressInImageUpload", value);
  }

  get isTpsActive() {
    return true;
  }

  private _isKsrAutoLoad = restoreFromLocalStorage("_isKsrAutoLoad", "feature", false);

  get isKsrAutoLoad() {
    return true;
  }

  @Action
  public setIsKsrAutoLoad(value: boolean) {
    this.context.commit("_mutateIsKsrAutoLoad", value);
  }

  @Mutation
  private _mutateIsKsrAutoLoad(value: boolean) {
    this._isKsrAutoLoad = value;
  }

  private _isReportV2 = restoreFromLocalStorage("_isReportV2", "feature", false);

  get isReportV2() {
    return this._isReportV2;
  }

  @Action
  public setIsReportV2(value: boolean) {
    this.context.commit("_mutateIsReportV2", value);
  }

  @Mutation
  private _mutateIsReportV2(value: boolean) {
    this._isReportV2 = value;
  }

  private _isTimeLine = restoreFromLocalStorage("_isTimeLine", "feature", false);

  get isTimeLine() {
    return true;
  }

  @Action
  public setIsTimeLine(value: boolean) {
    this.context.commit("_mutateIsTimeLine", value);
  }

  @Mutation
  private _mutateIsTimeLine(value: boolean) {
    this._isTimeLine = value;
  }

  // Feature flag for blueprint zoning in create dialog
  private _isBlueprintZoningEnabled = restoreFromLocalStorage("_isBlueprintZoningEnabled", "feature", true);

  get isBlueprintZoningEnabled() {
    return this._isBlueprintZoningEnabled;
  }

  @Action
  public setIsBlueprintZoningEnabled(value: boolean) {
    this.context.commit("_mutateIsBlueprintZoningEnabled", value);
  }

  @Mutation
  private _mutateIsBlueprintZoningEnabled(value: boolean) {
    this._isBlueprintZoningEnabled = value;
  }

  /**
   * Feature flag to use iframe player for videos (e.g., Mux Player).
   * Defaults to false so native <video> is used by default.
   */
  private _isVideoIframeEnabled = restoreFromLocalStorage("_isVideoIframeEnabled", "feature", false);

  /**
   * Returns true when iframe-based video playback is enabled.
   */
  get isVideoIframeEnabled(): boolean {
    return this._isVideoIframeEnabled;
  }

  @Mutation
  private _mutateIsVideoIframeEnabled(value: boolean) {
    this._isVideoIframeEnabled = value;
  }

  @Action
  public setIsVideoIframeEnabled(value: boolean) {
    this.context.commit("_mutateIsVideoIframeEnabled", value);
  }

  private _isDocumentV2Enabled = restoreFromLocalStorage("_isDocumentV2Enabled", "feature", false);

  /**
   * Returns true when Document V2 is enabled.
   */
  get isDocumentV2Enabled(): boolean {
    return this._isDocumentV2Enabled;
  }

  @Mutation
  private _mutateIsDocumentV2Enabled(value: boolean) {
    this._isDocumentV2Enabled = value;
  }

  @Action
  public setIsDocumentV2Enabled(value: boolean) {
    const flag = "isDocumentV2Enabled";
    this.trackFeatureFlag({ flag, value });
    this.context.commit("_mutateIsDocumentV2Enabled", value);
  }

  /** One-time dismissal state for the Document V2 promo banner, persisted in localStorage via vuex-persist. */
  private _isDocumentV2BannerDismissed = restoreFromLocalStorage("_isDocumentV2BannerDismissed", "feature", false);

  get isDocumentV2BannerDismissed(): boolean {
    return this._isDocumentV2BannerDismissed;
  }

  @Mutation
  private _mutateIsDocumentV2BannerDismissed(value: boolean) {
    this._isDocumentV2BannerDismissed = value;
  }

  @Action
  public setIsDocumentV2BannerDismissed(value: boolean) {
    this.context.commit("_mutateIsDocumentV2BannerDismissed", value);
  }

  /**
   * Feature flag for tags on entities (repair-estimate, clearance-approval, appraisal, clearance, rental).
   * Controls tag columns in tables, tag selection in detail views, and resource options in tag management.
   */
  private _isTagsEnabled = restoreFromLocalStorage("_isTagsEnabled", "feature", false);

  get isTagsEnabled(): boolean {
    return true;
  }

  @Mutation
  private _mutateIsTagsEnabled(value: boolean) {
    this._isTagsEnabled = value;
  }

  @Action
  public setIsTagsEnabled(value: boolean) {
    this.context.commit("_mutateIsTagsEnabled", value);
  }

  /**
   * Feature flag for automatic registration recognition in workitem flow
   */
  private _isRegistrationRecognitionEnabled = restoreFromLocalStorage(
    "_isRegistrationRecognitionEnabled",
    "feature",
    false
  );

  get isRegistrationRecognitionEnabled(): boolean {
    return this._isRegistrationRecognitionEnabled;
  }

  @Mutation
  private _mutateIsRegistrationRecognitionEnabled(value: boolean) {
    this._isRegistrationRecognitionEnabled = value;
  }

  @Action
  public setIsRegistrationRecognitionEnabled(value: boolean) {
    this.context.commit("_mutateIsRegistrationRecognitionEnabled", value);
  }

  /**
   * Feature flag for event recipients (notify driver + recipients table rows).
   */
  private _isEventRecipientsEnabled = restoreFromLocalStorage("_isEventRecipientsEnabled", "feature", false);

  get isEventRecipientsEnabled(): boolean {
    return this._isEventRecipientsEnabled;
  }

  @Mutation
  private _mutateIsEventRecipientsEnabled(value: boolean) {
    this._isEventRecipientsEnabled = value;
  }

  @Action
  public setIsEventRecipientsEnabled(value: boolean) {
    this.context.commit("_mutateIsEventRecipientsEnabled", value);
  }

  /**
   * Feature flag for locking the tax rate on line items when
   * the selected product has a tax rate configured.
   * Disabled by default.
   */
  private _isProductTaxLockEnabled = restoreFromLocalStorage("_isProductTaxLockEnabled", "feature", false);

  get isProductTaxLockEnabled(): boolean {
    return true;
  }

  @Mutation
  private _mutateIsProductTaxLockEnabled(value: boolean) {
    this._isProductTaxLockEnabled = value;
  }

  @Action
  public setIsProductTaxLockEnabled(value: boolean) {
    this.context.commit("_mutateIsProductTaxLockEnabled", value);
  }

  /**
   * Feature flag for the unified image and video gallery.
   */
  private _isUnifiedMediaGalleryEnabled = restoreFromLocalStorage("_isUnifiedMediaGalleryEnabled", "feature", false);

  /**
   * Returns true when unified media gallery is enabled.
   */
  get isUnifiedMediaGalleryEnabled(): boolean {
    return this._isUnifiedMediaGalleryEnabled;
  }

  @Mutation
  private _mutateIsUnifiedMediaGalleryEnabled(value: boolean) {
    this._isUnifiedMediaGalleryEnabled = value;
  }

  @Action
  public setIsUnifiedMediaGalleryEnabled(value: boolean) {
    this.context.commit("_mutateIsUnifiedMediaGalleryEnabled", value);
  }
}

export const FeatureModule = getModule(FeatureStore);

export interface IFleetFeature {
  isAppointmentEnabled: boolean;
}

export interface IDigitalSignatureFeature {
  isSignEnabled: boolean;
}

export interface IReportFeature {
  isTimelineEnabled: boolean;
  isSharingEnabled: boolean;
}

export interface IMarketplaceFeature {
  isMarketplaceEnabled: boolean;
  isInsuranceCheckEnabled: boolean;
}
